import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLabels } from '../context/LabelsContext';

const Profesionales = () => {
  const { user, loading: authLoading } = useAuth();
  const { getLabel, updateLabel } = useLabels();
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [tituloTemp, setTituloTemp] = useState('');
  const [profesionales, setProfesionales] = useState([]);
  const [profesionalesFiltrados, setProfesionalesFiltrados] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [accesoDenegado, setAccesoDenegado] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [limiteProfesionales, setLimiteProfesionales] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    email: '',
    telefono: '',
    documento_identidad: '',
    especialidad: '',
    titulo: '',
    anos_experiencia: '',
    color_calendario: '#3b82f6',
    sucursal_id: '',
    servicios: [],
    activo: true,
    acepta_citas: true
  });

  const coloresDisponibles = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', 
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'
  ];

  // Estilos para el mensaje de acceso denegado
  const styles = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
    },
    card: {
      backgroundColor: 'white',
      padding: '2rem 3rem',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      textAlign: 'center',
      maxWidth: '500px',
    },
    icon: {
      fontSize: '4rem',
      display: 'block',
      marginBottom: '1rem',
    },
    message: {
      color: '#6b7280',
      marginBottom: '1.5rem',
      lineHeight: '1.6',
    },
    suggestion: {
      fontSize: '0.9rem',
      color: '#9ca3af',
      marginBottom: '2rem',
    },
    button: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
  };

  useEffect(() => {
    if (authLoading) return;
    if (user?.tenant?.acceso_bloqueado === true) {
      setAccesoDenegado(true);
      setMensajeError('Tu suscripción está suspendida. Contacta al administrador.');
      setLoading(false);
      return;
    }
    const checkAccesoYcargarDatos = async () => {
      try {
        await Promise.all([
          api.get('/profesionales'),
          api.get('/servicios'),
          api.get('/sucursales'),
          api.get('/tenant/plan-info')
        ]).then(([profRes, servRes, sucRes, planRes]) => {
          setProfesionales(profRes.data);
          setProfesionalesFiltrados(profRes.data);
          setServicios(servRes.data);
          setSucursales(sucRes.data);
          const max = Number(planRes.data.plan?.max_profesionales || 0);
          setLimiteProfesionales(max);
          setLoading(false);
        });
      } catch (error) {
        if (error.response && error.response.status === 403) {
          setAccesoDenegado(true);
          setMensajeError(error.response.data.message || 'Tu plan no incluye profesionales.');
          setLoading(false);
        } else {
          toast.error('Error al cargar la página.');
          navigate('/');
        }
      }
    };

    checkAccesoYcargarDatos();
  }, [navigate, authLoading]);

  // Filtrar profesionales por búsqueda
  useEffect(() => {
    if (profesionales.length > 0) {
      const filtrados = profesionales.filter(p =>
        `${p.nombres} ${p.apellidos}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.especialidad && p.especialidad.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.email && p.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setProfesionalesFiltrados(filtrados);
    } else {
      setProfesionalesFiltrados([]);
    }
  }, [searchTerm, profesionales]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        anos_experiencia: formData.anos_experiencia ? parseInt(formData.anos_experiencia) : null,
        sucursal_id: formData.sucursal_id || null
      };

      if (editingId) {
        await api.put(`/profesionales/${editingId}`, data);
        toast.success('Profesional actualizado');
      } else {
        // Verificar duplicado por email o documento antes de crear
        const duplicado = profesionales.find(p =>
          (data.email && p.email === data.email) ||
          (data.documento_identidad && p.documento_identidad === data.documento_identidad)
        );
        if (duplicado) {
          toast.error('Ya existe un profesional con ese email o documento de identidad');
          return;
        }
        await api.post('/profesionales', data);
        toast.success('Profesional creado');
      }

      // Recargar datos — forma correcta de desestructurar respuesta axios
      const { data: nuevosProfs } = await api.get('/profesionales');
      setProfesionales(nuevosProfs);
      setProfesionalesFiltrados(nuevosProfs);
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar profesional');
    }
  };

  const handleEdit = async (profesional) => {
    try {
      const { data } = await api.get(`/profesionales/${profesional.id}`);
      setFormData({
        nombres: data.nombres,
        apellidos: data.apellidos,
        email: data.email || '',
        telefono: data.telefono || '',
        documento_identidad: data.documento_identidad || '',
        especialidad: data.especialidad || '',
        titulo: data.titulo || '',
        anos_experiencia: data.anos_experiencia || '',
        color_calendario: data.color_calendario || '#3b82f6',
        sucursal_id: data.sucursal_id || '',
        servicios: data.servicios ? data.servicios.map(s => s.servicio_id) : [],
        activo: data.activo,
        acepta_citas: data.acepta_citas
      });
      setEditingId(profesional.id);
      setShowModal(true);
    } catch (error) {
      toast.error('Error al cargar profesional');
    }
  };

  const handleDelete = (id) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.delete(`/profesionales/${id}`);
        toast.success('Profesional eliminado');
        const { data: nuevosProfs } = await api.get('/profesionales');
        setProfesionales(nuevosProfs);
        setProfesionalesFiltrados(nuevosProfs);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar');
      }
    }});
  };

  const handleServicioToggle = (servicioId) => {
    setFormData(prev => ({
      ...prev,
      servicios: prev.servicios.includes(servicioId)
        ? prev.servicios.filter(id => id !== servicioId)
        : [...prev.servicios, servicioId]
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      nombres: '',
      apellidos: '',
      email: '',
      telefono: '',
      documento_identidad: '',
      especialidad: '',
      titulo: '',
      anos_experiencia: '',
      color_calendario: '#3b82f6',
      sucursal_id: '',
      servicios: [],
      activo: true,
      acepta_citas: true
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen">
          <div className="loading"></div>
          <p>Cargando profesionales...</p>
        </div>
      </Layout>
    );
  }

  if (accesoDenegado) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.card}>
            <span style={styles.icon}>🔒</span>
            <h2>Acceso Restringido</h2>
            <p style={styles.message}>{mensajeError}</p>
            <p style={styles.suggestion}>
              Si crees que esto es un error, contacta al administrador del sistema para revisar los detalles de tu plan.
            </p>
            <button 
              style={styles.button} 
              onClick={() => navigate('/')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            👨‍💼{' '}
            {editandoTitulo ? (
              <input
                autoFocus
                value={tituloTemp}
                onChange={e => setTituloTemp(e.target.value)}
                onBlur={() => { updateLabel('profesionales', tituloTemp); setEditandoTitulo(false); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { updateLabel('profesionales', tituloTemp); setEditandoTitulo(false); }
                  if (e.key === 'Escape') setEditandoTitulo(false);
                }}
                style={{ fontSize: 'inherit', fontWeight: 'inherit', border: '1px dashed #2563eb', borderRadius: '6px', padding: '0.1rem 0.4rem', outline: 'none', background: '#f0f9ff', minWidth: '120px' }}
              />
            ) : (
              <span
                onClick={() => { setTituloTemp(getLabel('profesionales')); setEditandoTitulo(true); }}
                title="Click para editar el nombre"
                style={{ cursor: 'pointer', borderBottom: '1px dashed #9ca3af', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
              >
                {getLabel('profesionales')}
                <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>✏️</span>
              </span>
            )}
          </h2>
          {limiteProfesionales !== null && (
            <div style={{
              marginTop: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <div style={{
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                background: 'var(--background)',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px'
              }}>
                📊 Profesionales: {profesionales.length} / {limiteProfesionales}
              </div>
              {profesionales.length >= limiteProfesionales && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#dc2626',
                  background: '#fee2e2',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px'
                }}>
                  ⚠️ Límite alcanzado
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Nuevo {getLabel('profesionales').replace(/s$/, '')}
          </button>
        </div>
      </div>

      {/* Buscador */}
      <div className="card mb-3">
        <input
          type="text"
          className="form-input"
          placeholder={`🔍 Buscar ${getLabel('profesionales').replace(/s$/, '').toLowerCase()} por nombre, especialidad o email...`}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            padding: '0.75rem',
            fontSize: '1rem',
            backgroundColor: '#fafafa',
            width: '100%',
            transition: 'all 0.2s'
          }}
          onFocus={e => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.6)';
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={e => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
            e.target.style.backgroundColor = '#fafafa';
            e.target.style.boxShadow = 'none';
          }}
        />
        {searchTerm && (
          <div style={{
            marginTop: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            textAlign: 'right'
          }}>
            {profesionalesFiltrados.length} resultado(s) encontrado(s)
          </div>
        )}
      </div>

      {/* Contenido existente de profesionales */}
      {profesionalesFiltrados.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <span style={{ fontSize: '3rem' }}>👨‍💼</span>
          <h3 style={{ marginTop: '1rem' }}>
            {searchTerm ? `No se encontraron ${getLabel('profesionales').toLowerCase()}` : `No hay ${getLabel('profesionales').toLowerCase()} creados`}
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
            {searchTerm
              ? 'Intenta con otro término de búsqueda'
              : `Crea tu primer ${getLabel('profesionales').replace(/s$/, '').toLowerCase()} haciendo click en el botón "+ Nuevo ${getLabel('profesionales').replace(/s$/, '')}"`}
          </p>
        </div>
      ) : (
        <div className="grid grid-3">
          {profesionalesFiltrados.map(prof => (
            <div key={prof.id} className="card">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div 
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: prof.color_calendario
                    }}
                  />
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    {prof.nombres} {prof.apellidos}
                  </h3>
                </div>
                {!prof.activo && (
                  <span className="badge badge-danger">Inactivo</span>
                )}
              </div>

              {prof.especialidad && (
                <p style={{ 
                  color: 'var(--primary)', 
                  fontSize: '0.875rem', 
                  marginBottom: '0.75rem',
                  fontWeight: 500
                }}>
                  {prof.especialidad}
                </p>
              )}
              
              <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {prof.email && (
                  <p style={{ marginBottom: '0.25rem' }}>✉️ {prof.email}</p>
                )}
                {prof.telefono && (
                  <p style={{ marginBottom: '0.25rem' }}>📞 {prof.telefono}</p>
                )}
                {prof.sucursal_nombre && (
                  <p style={{ marginBottom: '0.25rem' }}>🏢 {prof.sucursal_nombre}</p>
                )}
                {prof.anos_experiencia && (
                  <p style={{ marginBottom: '0.25rem' }}>⭐ {prof.anos_experiencia} años exp.</p>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                {prof.acepta_citas ? (
                  <span className="badge badge-success">Acepta citas</span>
                ) : (
                  <span className="badge badge-warning">No acepta citas</span>
                )}
              </div>

              <div className="flex gap-2">
                {(user?.rol === 'admin' || user?.puede_modificar_profesionales) && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEdit(prof)}
                  >
                    ✏️ Editar
                  </button>
                )}
                {(user?.rol === 'admin' || user?.puede_eliminar_profesionales) && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(prof.id)}
                  >
                    🗑️ Eliminar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal para crear/editar profesional */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '100%', borderRadius: '16px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  👨‍💼
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    {editingId ? `Editar ${getLabel('profesionales').replace(/s$/, '')}` : `Nuevo ${getLabel('profesionales').replace(/s$/, '')}`}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {editingId ? `Modifica la información del ${getLabel('profesionales').replace(/s$/, '').toLowerCase()}` : `Completa los datos para agregar un nuevo ${getLabel('profesionales').replace(/s$/, '').toLowerCase()}`}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>

                {/* Datos personales */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Datos personales</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombres',              label: 'Nombres',             required: true, placeholder: 'Ej. Juan' },
                      { key: 'apellidos',             label: 'Apellidos',           required: true, placeholder: 'Ej. Pérez' },
                      { key: 'email',                 label: 'Email',               type: 'email',  placeholder: 'correo@ejemplo.com' },
                      { key: 'telefono',              label: 'Teléfono',            type: 'tel',    placeholder: '0999 999 999' },
                      { key: 'documento_identidad',   label: 'Documento de identidad',              placeholder: '1234567890' },
                    ].map(({ key, label, required, type, placeholder }) => (
                      <div key={key} className="form-group" style={key === 'documento_identidad' ? { gridColumn: '1 / -1' } : {}}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <input type={type || 'text'} className="form-input"
                          value={formData[key]}
                          onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                          required={!!required} placeholder={placeholder}
                          style={{ border: '2px solid rgba(59,130,246,0.3)', borderRadius: '8px', padding: '0.75rem', fontSize: '1rem', backgroundColor: '#fafafa', width: '100%' }}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Información profesional */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>💼 Información profesional</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    {[
                      { key: 'especialidad',    label: 'Especialidad',        placeholder: 'Barbero, Estilista...' },
                      { key: 'titulo',          label: 'Título profesional',  placeholder: 'Ej. Licenciado en...' },
                      { key: 'anos_experiencia', label: 'Años de experiencia', type: 'number', placeholder: '0' },
                    ].map(({ key, label, type, placeholder }) => (
                      <div key={key} className="form-group">
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>{label}</label>
                        <input type={type || 'text'} className="form-input"
                          value={formData[key]}
                          onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                          placeholder={placeholder} min={type === 'number' ? 0 : undefined} max={type === 'number' ? 50 : undefined}
                          style={{ border: '2px solid rgba(59,130,246,0.3)', borderRadius: '8px', padding: '0.75rem', fontSize: '1rem', backgroundColor: '#fafafa', width: '100%' }}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>
                    ))}
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Sucursal</label>
                      <select className="form-input" value={formData.sucursal_id}
                        onChange={(e) => setFormData({...formData, sucursal_id: e.target.value})}
                        style={{ border: '2px solid rgba(59,130,246,0.3)', borderRadius: '8px', padding: '0.75rem', fontSize: '1rem', backgroundColor: '#fafafa', width: '100%' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                      >
                        <option value="">Todas las sucursales</option>
                        {sucursales.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.6rem', display: 'block', fontSize: '0.875rem' }}>Color en calendario</label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {coloresDisponibles.map(color => (
                        <button key={color} type="button" onClick={() => setFormData({...formData, color_calendario: color})}
                          style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: color, cursor: 'pointer',
                            border: formData.color_calendario === color ? '3px solid #111827' : '2px solid #e5e7eb' }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Servicios y configuración */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#fdf4ff', color: '#7c3aed', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>🛠️ Servicios y configuración</span>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.6rem', display: 'block', fontSize: '0.875rem' }}>Servicios que ofrece</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem',
                      maxHeight: '160px', overflowY: 'auto', padding: '0.75rem',
                      border: '2px solid rgba(59,130,246,0.2)', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                      {servicios.map(s => (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                          <input type="checkbox" checked={formData.servicios.includes(s.id)} onChange={() => handleServicioToggle(s.id)} />
                          <span>{s.nombre}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '2rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                      <input type="checkbox" checked={formData.activo} onChange={(e) => setFormData({...formData, activo: e.target.checked})} style={{ width: '16px', height: '16px' }} />
                      {getLabel('profesionales').replace(/s$/, '')} activo
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                      <input type="checkbox" checked={formData.acepta_citas} onChange={(e) => setFormData({...formData, acepta_citas: e.target.checked})} style={{ width: '16px', height: '16px' }} />
                      Acepta citas
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem', flexShrink: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingId ? 'Guardar cambios' : `+ Crear ${getLabel('profesionales').replace(/s$/, '').toLowerCase()}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Profesionales;
